import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useXBookmarks } from '@/hooks/useXBookmarks';
import { Loader2 } from 'lucide-react';

interface XBookmarksSettingsProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function XBookmarksSettings({ open, onOpenChange }: XBookmarksSettingsProps) {
  const {
    status,
    isLoading,
    isSyncing,
    fetchStatus,
    connect,
    disconnect,
    syncNow,
  } = useXBookmarks();

  const [backfillMonths, setBackfillMonths] = useState('6');

  useEffect(() => {
    if (open) {
      fetchStatus();
    }
  }, [open, fetchStatus]);

  const lastSyncTime = status?.last_sync?.completed_at
    ? new Date(status.last_sync.completed_at).toLocaleString()
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
            X (Twitter) Bookmarks
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : !status?.connected ? (
            /* Not connected state */
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Connect your X account to automatically sync your bookmarks into
                Mindfull. Tweets appear as rich preview cards in a dedicated "X
                Bookmarks" board.
              </p>
              <Button onClick={connect} className="w-full">
                <svg
                  className="w-4 h-4 mr-2"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
                Connect X Account
              </Button>
            </div>
          ) : (
            /* Connected state */
            <div className="space-y-4">
              {/* Connection info */}
              <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/50 border border-border/50">
                <div className="w-10 h-10 rounded-full bg-foreground/10 flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-foreground"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm text-foreground">
                    @{status.username}
                  </div>
                  <div className="text-xs text-muted-foreground">Connected</div>
                </div>
                <div className="w-2 h-2 rounded-full bg-success" />
              </div>

              {/* Last sync info */}
              {status.last_sync && (
                <div className="text-xs text-muted-foreground space-y-1">
                  <div>
                    Last sync: {lastSyncTime}
                  </div>
                  <div>
                    {status.last_sync.tweets_inserted} tweet
                    {status.last_sync.tweets_inserted !== 1 ? 's' : ''} synced (
                    {status.last_sync.sync_type})
                  </div>
                  {status.last_sync.error && (
                    <div className="text-destructive">
                      Error: {status.last_sync.error}
                    </div>
                  )}
                </div>
              )}

              {/* Backfill period selector */}
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">
                  Sync period
                </label>
                <Select value={backfillMonths} onValueChange={setBackfillMonths}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Last 1 month</SelectItem>
                    <SelectItem value="3">Last 3 months</SelectItem>
                    <SelectItem value="6">Last 6 months</SelectItem>
                    <SelectItem value="12">Last 12 months</SelectItem>
                    <SelectItem value="24">Last 2 years</SelectItem>
                    <SelectItem value="120">All time (max ~800 tweets)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  X API returns up to ~800 most recent bookmarks regardless of time period.
                </p>
              </div>

              {/* Action buttons */}
              <div className="flex gap-2">
                <Button
                  onClick={() => syncNow('backfill', parseInt(backfillMonths))}
                  disabled={isSyncing}
                  className="flex-1"
                >
                  {isSyncing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Syncing...
                    </>
                  ) : (
                    'Sync Now'
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={disconnect}
                  disabled={isSyncing}
                >
                  Disconnect
                </Button>
              </div>

              <p className="text-xs text-muted-foreground">
                Bookmarks sync daily at 7am ET. Disconnecting removes your X
                credentials but keeps synced items.
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
